import _ from 'lodash';
import config from 'client/shared/config';
import { toast } from 'react-toastify';

import {
	observable,
	action,
	computed,
	runInAction
} from 'mobx';

import apiStore from './api-store';
import BaseStore from './base-store';
import Playlist from './playlist';
import songsStore from './songs-store';
import Song from './song';

const { routes } = config;

class PlaylistStore extends BaseStore {
	apiRoute = config.getRoute('playlists');

	/**
	 * Observables & Computeds
	 */

	@observable
	_playlistModalOpen = false;

	@computed
	get playlistModalOpen () {
		return this._playlistModalOpen;
	}

	set playlistModalOpen (isOpen) {
		this._playlistModalOpen = isOpen;
	}
	
	@computed
	get showOnFrontPage () {
		return this._showOnFrontPage;
	}

	set showOnFrontPage (show) {
		this._showOnFrontPage = show;
	}

	@computed
	get playlists () {
		return this._items;
	}

	set playlists (playlists) {
		this._items = playlists;
	}

	@computed
	get hfmPicks () {
		return _(this.playlists)
			.filter({ isHFMPicks: true })
			.sortBy('HFMPicksPosition')
			.value();
	}

	@computed
	get hfmRadio () {
		return _(this.playlists)
			.filter({ isHFMRadio: true})
			.sortBy('label')
			.value();
	}

	@observable
	_currentPlaylist = {};

	@computed
	get currentPlaylist () {
		return this._currentPlaylist;
	}

	set currentPlaylist (currentPlaylist) {
		this._currentPlaylist = _.isString(currentPlaylist) ?
			this.getItemByID(currentPlaylist) : currentPlaylist;
	}

	/**
	 * Actions
	 */

	@action
	setCurrentPlaylist (playlist) {
		songsStore.currentSong = null;
		this.currentPlaylist = playlist;
	}

	async fetchMemberHomeLists () {
		const { data } = await apiStore.ax
			.get(routes.playlistMemberHomeLists);
		return data;
	}

	@action
	async savePlaylist (playlist, opts = {}) {
		const { silent = false } = opts;

		this.isLoading = silent !== true;

		try {

			const route = _.isEmpty(playlist.id) ? this.apiRoute : routes.playlistCreate;
			await this.save(playlist, { route });
			toast.success('Playlist saved!');
		} catch (err) {

			toast.error('Error saving playlist!');
			console.error('[ERROR: PlaylistStore.savePlaylist]', err);
		} finally {

			this.isLoading = false;
		}
	}

	@action
	async savePlaylistArtwork (playlist, opts = {}) {
		const { id } = playlist;
		const { silent = false } = opts;

		playlist.imageSavePath = `playlist-images/${id}`;
		this.isLoading = silent !== true;

		try {

			const route = routes.playlistArtworkUpload.replace(/\:id/, id);
			await this.save(payload, { route });
			toast.success('Playlist saved!');
		} catch (err) {

			toast.error('Error saving playlist!');
			throw err;
		} finally {

			this.isLoading = false;
		}
	}

	@action
	async generateHFMPicks () {
		const hfmPicksPayload = [
			{
				"title": "Summer Driving",
				"songs": [],
				"isHFMPicks": true,
				"HFMPicksPosition": 1
			},
			{
				"title": "Night Driving",
				"songs": [],
				"isHFMPicks": true,
				"HFMPicksPosition": 2
			},
			{
				"title": "Fall Driving",
				"songs": [],
				"isHFMPicks": true,
				"HFMPicksPosition": 3
			}
		];

		try {

			const route = routes.playlist.replace(/\/\:id/, '');
			const data = await this.save(hfmPicksPayload, { route });
			runInAction('Update after generating HFM Picks', () => {
				const hfmPicksList = data.map((playlist) => new Playlist(this, playlist));
				this.playlists = this.playlists.slice().concat(hfmPicksList);
				toast.success('HFM Picks generated!');
			});
		} catch (err) {

			throw err;
		}
	}

	@action
	addSongToPlaylist (playlistID, songID) {
		if (_.isNull(songID)) {

			return;
		}

		if (_.isEmpty(playlistID)) {

			const songs = this.data.playlist.get('songs');
			songs.unshift(songID);
			this.data.playlist.set('songs', songs);
			return;
		}

		const index = this.getItemIndex({ id: playlistID });

		if (_.isEmpty(this.playlists[index].songs)) {

			this.playlists[index].songs = observable([]);
		}

		this.playlists[index].songs.unshift(songID);
	}

	@action
	removeSongFromPlaylist (playlistID, songID) {
		if (_.isEmpty(playlistID)) {

			const songs = this.data.playlist.get('songs')
			this.data.playlist
				.replace('songs', songs.filter((id) => id !== songID))
			return;
		}

		const index = this.getItemIndex({ id: playlistID });

		this.playlists[index].songs =
			this.playlists[index].songs.filter((id) => id !== songID);
	}

	@action
	reorderPlaylist (result, playlistID) {
		const index = this.getItemIndex({ id: playlistID });

		const { source: { index: startIndex }, destination: { index: endIndex } } = result;
		const reordered = Array.from(this.playlists[index].songs.slice());
		const [ removed ] = reordered.splice(startIndex, 1);

		reordered.splice(endIndex, 0, removed);
		this.playlists[index].songs = reordered;
	}

	
	@action
	onFetchSuccess (data) {
		data.forEach((p) => {
			p.songs = p.songs.map((songData) => new Song(songsStore, songData));
		})
		this._items = data;
	}

	@action
	async search (search) {
		try {

			const { data } = await apiStore.ax
				.get(routes.searchGenres.replace(/:search/, search));
			this.searchResults = data;
		} catch (err) {

			console.log('[FETCH GENRES ERROR]', err);
			return [];
		}
	}

	@action
	async deletePlaylist (playlist) {
		await this.delete(playlist);
		toast.success('Playlist deleted!');
	}
}

const playlistStore = new PlaylistStore();
export default playlistStore;
